import { Role } from '@/client/roles';
import { User } from '@/client/users';
import { LoadingStatuses } from '@/common/constants';
import { useApprovePendingUser, useDenyPendingUser } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { AppButton } from '@/ui/buttons';
import { Dropdown } from 'primereact/dropdown';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: var(--medium-big-padding);
  padding: var(--semi-big-padding);
  margin-bottom: var(--semi-big-padding);
  border-radius: var(--box-medium-border-radius);
  background-color: var(--yellow-pale);
  border: 1px solid var(--yellow-dark-22);
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

const Heading = styled.h3`
  font-size: var(--body-font-size);
  font-weight: 600;
  line-height: var(--body-line-height);
  margin: 0;
`;

const Description = styled.p`
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: var(--small-line-height);
  margin: 0;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
  flex-shrink: 0;
`;

const StyledDropdown = styled(Dropdown)`
  height: var(--default-input-height);
  overflow: hidden;
  border-radius: var(--default-border-radius);
  border-color: var(--yellow-dark-22);
  background-color: var(--white-main);

  .p-dropdown-label {
    font-size: var(--small-font-size);
    font-weight: 500;
    background: inherit;
  }
`;

type PendingUserBannerProps = {
  pendingUser: User;
  accountId: string;
  roles: Role[];
};

export const PendingUserBanner: React.FC<PendingUserBannerProps> = ({
  pendingUser,
  accountId,
  roles,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { approve, isLoading: isApproving } = useApprovePendingUser();
  const { deny, isLoading: isDenying } = useDenyPendingUser();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const invalidateUsers = () => {
    queryClient.invalidateQueries('users');
  };

  const handleApprove = async () => {
    if (!selectedRoleId) return;

    try {
      await approve({
        userId: pendingUser.id,
        accountId,
        roleId: selectedRoleId,
      });
      toast?.success(t('toast.success'), t('pendingAccess.toast.approved'));
      invalidateUsers();
    } catch {
      toast?.error(t('toast.error'), t('error.generic'));
    }
  };

  const handleDeny = async () => {
    try {
      await deny({ userId: pendingUser.id, accountId });
      toast?.success(t('toast.success'), t('pendingAccess.toast.denied'));
      await queryClient.refetchQueries('users');
    } catch {
      toast?.error(t('toast.error'), t('error.generic'));
      setShowConfirm(false);
    }
  };

  const roleOptions = roles.map((role) => ({
    label: role.name,
    value: role.id,
  }));

  if (showConfirm) {
    return (
      <Banner>
        <TextContent>
          <Heading>{t('pendingAccess.banner.removeConfirm.title')}</Heading>
          <Description>
            {t('pendingAccess.banner.removeConfirm.message')}
          </Description>
        </TextContent>
        <ActionsRow>
          <AppButton
            label={t('pendingAccess.banner.removeConfirm.confirm')}
            onClick={handleDeny}
            isDisabled={isDenying}
            state={isDenying ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          />
          <AppButton
            label={t('pendingAccess.banner.removeConfirm.cancel')}
            onClick={() => setShowConfirm(false)}
            severity="simple"
          />
        </ActionsRow>
      </Banner>
    );
  }

  return (
    <Banner>
      <TextContent>
        <Heading>{t('pendingAccess.banner.heading')}</Heading>
        <Description>
          {t('pendingAccess.banner.description', {
            name: `${pendingUser.firstName} ${pendingUser.lastName}`,
          })}
        </Description>
      </TextContent>
      <ActionsRow>
        <StyledDropdown
          value={selectedRoleId}
          options={roleOptions}
          onChange={(e) => setSelectedRoleId(e.value)}
          placeholder={t('pendingAccess.banner.chooseRole')}
        />
        <AppButton
          label={t('pendingAccess.banner.approve')}
          onClick={handleApprove}
          isDisabled={!selectedRoleId || isApproving}
          state={isApproving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
        />
        <AppButton
          label={t('pendingAccess.banner.remove')}
          onClick={() => setShowConfirm(true)}
          isDisabled={isDenying}
          severity="simple"
        />
      </ActionsRow>
    </Banner>
  );
};
